<template>
    <div class="upload-photo-container tw-min-h-64">
        <Upload
            ref="upload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :on-success="handleSuccess"
            :on-error="handleError"
            :on-progress="handleProgress"
            :before-upload="beforeUpload"
            :show-upload-list="false"
            accept="image/*"
        >
            <div class="photo-preview" v-if="currentPhoto">
                <img :src="currentPhoto" alt="Branch Photo" />
                <div class="photo-overlay">
                    <Icon type="ios-camera" size="40" />
                    <p>{{ $t("branch.clickToChange") }}</p>
                </div>
            </div>

            <div class="upload-placeholder" v-else>
                <Icon type="ios-image-outline" size="60" />
                <p>{{ $t("branch.clickToUpload") }}</p>
                <Button
                    icon="ios-cloud-upload-outline"
                    :loading="uploading"
                >
                    {{
                        uploading
                            ? $t("branch.uploading")
                            : $t("branch.selectPhoto")
                    }}
                </Button>
            </div>
        </Upload>

        <div class="progress-bar" v-if="uploading">
            <Progress :percent="uploadPercent" status="active" />
        </div>
    </div>
</template>

<script>
import VueCookies from "vue-cookies";

export default {
    name: "UploadPhoto",
    props: {
        branch: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            uploading: false,
            uploadPercent: 0,
            currentPhoto: null
        };
    },
    computed: {
        uploadUrl() {
            if (!this.branch || !this.branch.branch_id) {
                return "";
            }
            return `${process.env.VUE_APP_PROXY}/api/branches/${this.branch.branch_id}/upload-certificate-photo`;
        },
        uploadHeaders() {
            const token = VueCookies.get("token");
            return {
                Authorization: `Bearer ${token}`
            };
        }
    },
    mounted() {
        this.loadPhoto();
    },
    watch: {
        branch: {
            handler() {
                this.loadPhoto();
            },
            deep: true
        }
    },
    methods: {
        loadPhoto() {
            if (this.branch && this.branch.branch_certificate_photo) {
                this.currentPhoto = this.branch.branch_certificate_photo;
            } else {
                this.currentPhoto = null;
            }
        },
        beforeUpload(file) {
            const isImage = file.type.indexOf("image/") === 0;
            const isLt5M = file.size / 1024 / 1024 < 5;

            if (!isImage) {
                this.$Message.error(this.$t("branch.mustBeImage"));
                return false;
            }
            if (!isLt5M) {
                this.$Message.error(this.$t("branch.imageTooLarge"));
                return false;
            }
            return true;
        },
        handleProgress(event) {
            this.uploading = true;
            this.uploadPercent = parseInt(event.percent);
        },
        handleSuccess(response) {
            this.uploading = false;
            this.uploadPercent = 0;

            if (response.success) {
                this.$Message.success(this.$t("branch.uploadSuccess"));
                this.currentPhoto = response.data.branch_certificate_photo;
                this.$emit("on-success", response.data);
            } else {
                this.$Message.error(
                    response.message || this.$t("branch.uploadFailed")
                );
            }
        },
        handleError(error) {
            this.uploading = false;
            this.uploadPercent = 0;
            this.$Message.error(this.$t("branch.uploadFailed"));
            console.error("Upload error:", error);
        }
    }
};
</script>

<style scoped lang="scss">
.upload-photo-container {
    padding: 10px;

    .photo-preview {
        position: relative;
        cursor: pointer;
        border-radius: 4px;
        overflow: hidden;
        
        img {
            width: 100%;
            max-height: 400px;
            display: block;
            object-fit: contain;
        }

        .photo-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s;

            i {
                color: #fff;
                margin-bottom: 10px;
            }

            p {
                color: #fff;
                font-size: 14px;
                margin: 0;
            }
        }

        &:hover .photo-overlay {
            opacity: 1;
        }
    }

    .upload-placeholder {
        padding: 42px 160px;
        background: #f8f8f9;
        border: 2px dashed #dcdee2;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;
        text-align: center;

        &:hover {
            border-color: #2d8cf0;
            background: #f0f7ff;
        }

        i {
            color: #808695;
            margin-bottom: 10px;
        }

        p {
            margin: 10px 0;
            color: #515a6e;
            font-size: 14px;
        }
    }

    .upload-tips {
        margin-top: 15px;
        text-align: center;

        p {
            margin: 5px 0;
            font-size: 12px;
            color: #808695;
        }
    }

    .progress-bar {
        margin-top: 20px;
    }
}
</style>